import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', path: '/' },
  { label: '分类', path: '/category' },
  { label: '演员', path: '/actors' },
  { label: '影评', path: '/reviews' },
  { label: '搜索', path: '/search' },
  { label: '关于', path: '/about' },
  { label: '联系', path: '/contact' },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = keyword.trim();
    if (!trimmed) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    setKeyword('');
    setSearchOpen(false);
    setMenuOpen(false);
  };

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-800/80 bg-background-950/90 backdrop-blur-md">
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 cursor-pointer shrink-0">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-600 text-background-50 font-bold text-lg">
              <i className="ri-clapperboard-fill" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-lg font-bold text-foreground-50 font-heading tracking-wide">成人短剧</span>
              <span className="text-[10px] text-foreground-500 mt-0.5">精选成人向短篇影视剧</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3.5 py-2 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  isActive(item.path)
                    ? 'text-primary-400 font-medium'
                    : 'text-foreground-300 hover:text-foreground-50 hover:bg-background-800/60'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-flex items-center rounded-md bg-accent-900/70 border border-accent-700/60 px-2 py-1 text-[11px] font-semibold text-accent-200">
              18+
            </span>
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="搜索"
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-300 hover:text-primary-400 hover:bg-background-800/60 transition-colors cursor-pointer"
            >
              <i className="ri-search-line text-lg" />
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="菜单"
              className="md:hidden flex h-9 w-9 items-center justify-center rounded-md text-foreground-200 hover:bg-background-800/60 transition-colors cursor-pointer"
            >
              <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
            </button>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={submitSearch} className="pb-3 animate-fade-in">
            <div className="relative">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-500 text-base" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索短剧、演员或影评关键词…"
                className="w-full rounded-md bg-background-900 border border-background-700 py-2.5 pl-10 pr-4 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none"
              />
            </div>
          </form>
        )}
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-800 bg-background-950/98 animate-fade-in">
          <nav className="flex flex-col p-3">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-3 rounded-md text-sm transition-colors cursor-pointer ${
                  isActive(item.path)
                    ? 'text-primary-400 font-medium bg-background-900'
                    : 'text-foreground-300 hover:bg-background-900'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}